<template>
    <div :class="namespaceClass('')">
        <div :class="namespaceClass('__taxes')" v-if="includeTax">
            <div :class="namespaceClass('__taxes-information')">
                <mds-section :size="8" :title="getUILabel('taxes_information')" bold border="tertiary">
                    <template v-slot:default>
                        <mds-form size="small">
                            <mds-fieldset>
                                <mds-fieldset :legend="getUILabel('taxes_filing_status')" variation="radio-group" size="small">
                                    <mds-radio-button
                                        v-for="(item, index) in filingStatusOptions"
                                        :e2e="`taxes-filing-status-${index}`"
                                        v-model="taxFilingStatus"
                                        :key="index"
                                        :value="item.value"
                                        size="small"
                                    >
                                        {{ item.label }}
                                    </mds-radio-button>
                                </mds-fieldset>
                                <mds-input
                                    :label="`${getUILabel('taxes_taxable_income')} ${$store.getters['settings/portfolioCurrencySymbol']}`"
                                    :value="formatNumber(taxableIncome)"
                                    e2e="taxes-income"
                                    size="small"
                                    @change="handleTaxableIncomeChanged"
                                    @input="handleInput"
                                ></mds-input>
                                <mds-fieldset :legend="getUILabel('taxes_pay_option')" variation="radio-group" size="small">
                                    <mds-radio-button
                                        v-for="(item, index) in taxPayTypeOptions"
                                        :e2e="`taxes-pay-type-${index}`"
                                        v-model="taxPayType"
                                        :key="index"
                                        :value="item.value"
                                        size="small"
                                    >
                                        {{ item.label }}
                                    </mds-radio-button>
                                </mds-fieldset>
                            </mds-fieldset>
                        </mds-form>
                    </template>
                </mds-section>
            </div>
            <div :class="namespaceClass('__taxes-rate')">
                <mds-section :size="8" :title="getUILabel('taxes_rate')" bold border="tertiary">
                    <template v-slot:default>
                        <mds-form size="small">
                            <mds-fieldset hidden-legend>
                                <mds-input
                                    :label="`${getUILabel('taxes_federal_income')} %`"
                                    :value="formatNumber(federalTaxRate)"
                                    e2e="federal-tax-income"
                                    size="small"
                                    @change="handleFederalIncomeChanged"
                                    @input="handleInput"
                                ></mds-input>
                                <mds-input
                                    :label="getUILabel('taxes_capital_gain')"
                                    :value="formatNumber(capgainTaxRate)"
                                    e2e="capital-gain"
                                    size="small"
                                    @change="handleCapitalGainChanged"
                                    @input="handleInput"
                                ></mds-input>
                                <mds-input
                                    :label="getUILabel('taxes_state')"
                                    :value="formatNumber(stateTaxRate)"
                                    e2e="state-tax"
                                    size="small"
                                    @change="handleStateChanged"
                                    @input="handleInput"
                                ></mds-input>
                            </mds-fieldset>
                        </mds-form>
                    </template>
                </mds-section>
            </div>
        </div>
    </div>
</template>

<script>
import HypoVueBaseMixin from "hypo-vue-base-mixin";
import { mapState } from "vuex";
import * as types from "../store/mutation-types.js";
import { isNumber, parseNumber } from "@/util";
import MwcSharedMixin from "@/mixins/MwcSharedMixin";
import MdsSection from "@mds/section";
import MdsForm from "@mds/form";
import MdsFieldset from "@mds/fieldset";
import MdsInput from "@mds/input";
import MdsRadioButton from "@mds/radio-button";

export default {
    name: "taxes-content",
    components: {
        MdsSection,
        MdsForm,
        MdsFieldset,
        MdsInput,
        MdsRadioButton
    },
    mixins: [HypoVueBaseMixin, MwcSharedMixin],
    data() {
        return {
            defaultConfig: {
                labels: this.$store.state.labels.labels
            }
        };
    },
    created() {
        this.$store.dispatch("taxes/getTaxrate", { fetch: this.$fetch });
    },
    computed: {
        ...mapState("taxes", ["filingStatuses", "includeTax", "taxableIncome", "federalTaxRate", "stateTaxRate", "capgainTaxRate"]),
        taxFilingStatus: {
            get() {
                return this.$store.state.taxes.taxFilingStatus;
            },
            set(value) {
                this.$store.commit(`taxes/${types.SET_TAXFILINGSTATUS}`, value);
            }
        },
        taxPayType: {
            get() {
                return this.$store.state.taxes.taxPayType;
            },
            set(value) {
                this.$store.commit(`taxes/${types.SET_TAXPAYTYPE}`, value);
            }
        },
        filingStatusOptions() {
            return this.filingStatuses.map(item => {
                return {
                    label: item.filingStatusName,
                    value: item.filingStatusId
                };
            });
        },
        taxPayTypeOptions() {
            return this.$store.getters["taxes/taxPayTypes"].map(item => {
                return {
                    label: this.getUILabel(item.labelId),
                    value: item.value
                };
            });
        }
    },
    methods: {
        formatIntlNumber(value) {
            return this.formatNumberSymbol(this.formatter.number(value, "shortDecimal"));
        },
        handleTaxableIncomeChanged(e) {
            let num = e.target.value;

            // if the input is not number or num < 0, not need to update
            if (!isNumber(num) || parseNumber(num) < 0) {
                e.target.value = this.formatIntlNumber(this.taxableIncome);
                return;
            }

            num = parseNumber(num);
            this.$store.commit(`taxes/${types.SET_TAXABLEINCOME}`, num);
        },
        handleFederalIncomeChanged(e) {
            let num = e.target.value;

            // if the input is not number or num < 0 or num > 100, not need to update
            if (!isNumber(num) || parseNumber(num) < 0 || parseNumber(num) > 100) {
                e.target.value = this.formatIntlNumber(this.federalTaxRate);
                return;
            }

            num = parseNumber(num);
            this.$store.commit(`taxes/${types.SET_FEDERALTAXRATE}`, num);
        },
        handleStateChanged(e) {
            let num = e.target.value;

            // if the input is not number or num < 0 or num > 100, not need to update
            if (!isNumber(num) || parseNumber(num) < 0 || parseNumber(num) > 100) {
                e.target.value = this.formatIntlNumber(this.stateTaxRate);
                return;
            }

            num = parseNumber(num);
            this.$store.commit(`taxes/${types.SET_STATETAXRATE}`, num);
        },
        handleCapitalGainChanged(e) {
            let num = e.target.value;

            // if the input is not number or num < 0 or num > 100, not need to update
            if (!isNumber(num) || parseNumber(num) < 0 || parseNumber(num) > 100) {
                e.target.value = this.formatIntlNumber(this.capgainTaxRate);
                return;
            }

            num = parseNumber(num);
            this.$store.commit(`taxes/${types.SET_CAPGAINTAXRATE}`, num);
        },
        handleInput(value, e) {
            let num;
            const notNumberPat = /[^\d.,]+/g;
            if (notNumberPat.test(value)) {
                num = value.replace(notNumberPat, "");
            }
            const decimalPat = /^(.*\.[\d]{2})[\d]+/; // keep two decimal places
            if (decimalPat.test(value)) {
                num = this.formatIntlNumber(parseNumber(value.match(decimalPat)[1]));
            }
            if (num !== undefined) {
                e.target.value = num;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.taxes-content {
    &__taxes {
        display: flex;
    }
    &__taxes-information {
        flex-basis: 50%;
        padding-right: 16px;
    }
    &__taxes-rate {
        flex-basis: 50%;
        padding-left: 16px;
    }
}
</style>
